import { Fragment, useEffect, useCallback, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import { Badge, Inline, Heading, Stack, Text } from '@leroy-merlin-br/backyard-react-ui'

import { Spinner } from 'reactGarden/Spinner'

import { Actions as UiActions } from 'catalog/redux/ui'
import { Actions as SkusActions } from 'catalog/redux/modules/skus'

import * as S from './styled'
import { SellersList, SellersListButton } from './'

const Buybox = ({ isAssistedSale, limitQuantity, product }) => {
  const { isRequest, skus, selectedSeller } = useSelector(
    state => state.skus,
    shallowEqual
  )
  const isDrawerOpen = useSelector(state => state.ui.sellersDrawerOpen)

  const dispatch = useDispatch()

  const drawerRef = useRef()

  const shouldRenderSellersList = useMemo(() => skus.length > 3, [skus.length])

  const handleSellerSelect = useCallback(
    sku => {
      dispatch(SkusActions.selectSku(sku))
    },
    [dispatch]
  )

  const openDrawer = () => {
    if (!isDrawerOpen) {
      disableBodyScroll(drawerRef.current)
      dispatch(UiActions.toggleSellersDrawer())
    }
  }

  const closeDrawer = () => {
    if (isDrawerOpen) {
      enableBodyScroll(drawerRef.current)
      dispatch(UiActions.toggleSellersDrawer())
    }
  }

  useEffect(() => {
    handleSellerSelect(skus[0])
  }, [handleSellerSelect, skus])

  if (isRequest) return <Spinner />

  return (
    <Fragment>
      <S.Wrapper>
        <Stack spacing="bit">
          {
            limitQuantity && (
              <Text as="div" size="kilo" bold noMargin>
                {limitQuantity}
              </Text>
            )
          }

          {
            skus.length <= 1 && !product.isFreeShipping && !isAssistedSale && (
              <Badge color="green">Frete grátis</Badge>
            )
          }

          {skus.length > 1 && (
            <ul>
              {skus.slice(0, 3).map((sku, index) => {
                return (
                  <li key={sku.id}>
                    <S.OptionInput
                      type="radio"
                      id={`buybox_option_radio_${index}`}
                      name="buybox-option-radio"
                      autoComplete="off"
                      checked={selectedSeller?.id === sku.id}
                      onChange={() => handleSellerSelect(sku)}
                    />

                    <S.OptionLabel
                      htmlFor={`buybox_option_radio_${index}`}
                      isSelected={selectedSeller?.id === sku.id}
                    >
                      <S.OptionPrice as="div" size="giga" bold noMargin>
                        <Inline spacing="bit" justify="left" align="bottom" noWrap>
                          <Heading as="h6" size="giga" noMargin bold>
                            R$ {sku.price.to.integers},{sku.price.to.decimals}
                          </Heading>

                          <Text as="span" size="kilo" noMargin>
                            {product.unit}
                          </Text>
                        </Inline>
                      </S.OptionPrice>

                      <S.OptionSeller title={sku.shop.name}>
                        {sku.shop.name}
                      </S.OptionSeller>

                      {product.isFreeShipping && !isAssistedSale && (
                        <Badge color="green">Frete grátis</Badge>
                      )}
                    </S.OptionLabel>
                  </li>
                )
              })}
              {/* TODO: componente pode pegar o unit pelo redux quando salvar produto no estado */}
              {shouldRenderSellersList && (
                <SellersListButton openDrawer={openDrawer} unit={product.unit} />
              )}
            </ul>
          )}
        </Stack>
      </S.Wrapper>

      {shouldRenderSellersList && (
        <SellersList
          ref={drawerRef}
          productId={product.id}
          productName={product.name}
          productImg={product.img}
          closeDrawer={closeDrawer}
        />
      )}
    </Fragment>
  )
}

Buybox.propTypes = {
  isAssistedSale: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired,
  limitQuantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  product: PropTypes.shape({
    isFreeShipping: PropTypes.bool,
    unit: PropTypes.string.isRequired,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    img: PropTypes.string.isRequired
  }).isRequired
}

export default Buybox
